/**
 * Capacitor plugin provides a web browser view that allows you to load any web page externally.
 * Reference docs used to derive types & behavior:
 *   https://www.npmjs.com/package/@capacitor/inappbrowser
 */

import { useUserStore } from "@/shared/store/user";
import {
  AndroidAnimation,
  AndroidSystemBrowserOptions,
  AndroidViewStyle,
  DismissStyle,
  IOSAnimation,
  IOSSystemBrowserOptions,
  IOSViewStyle,
  SystemBrowserOptions,
  ToolbarPosition,
  WebViewOptions
} from "./types";
import { CapacitorPluginsBase } from "./capacitorPluginsBase";

class InAppBrowser extends CapacitorPluginsBase {
  /**
   * Opens the web content of the given URL in your mobile app using a custom web view within your application.
   * @param model The parameters to open the URL in the web view
   */
  async openInWebView (url: string): Promise<void> {
    const webViewOptions: WebViewOptions = {
      showURL: false,
      showToolbar: true,
      clearCache: false,
      clearSessionCache: false,
      mediaPlaybackRequiresUserAction: true,
      closeButtonText: "Close",
      toolbarPosition: ToolbarPosition.TOP,
      showNavigationButtons: true,
      leftToRight: true,
      customWebViewUserAgent: null,
      android: {
        allowZoom: true,
        hardwareBack: true,
        pauseMedia: true,
        isIsolated: false
      },
      iOS: {
        allowOverScroll: false,
        enableViewportScale: true,
        allowInLineMediaPlayback: false,
        surpressIncrementalRendering: false,
        viewStyle: IOSViewStyle.PAGE_SHEET,
        animationEffect: IOSAnimation.COVER_VERTICAL,
        allowsBackForwardNavigationGestures: true,
      }
    };
    const inAppBrowserRef = await this.plugins.InAppBrowser.openInWebView({
      url,
      options: webViewOptions
    });
    // event fires when the InAppBrowser finishes loading a URL
    await inAppBrowserRef.addListener("browserPageLoaded", loaded);
    // event fires when the InAppBrowser window is closed.
    await inAppBrowserRef.addListener("browserClosed", exited);

    function loaded () {
      const userStore = useUserStore();
      userStore.activeUserPing();
    }

    function exited () {
      inAppBrowserRef.removeAllListeners();
    }
  }
  /**
   * Opens the web content of the given URL in your mobile app, using SafariViewController for iOS and Custom Tabs for Android.
   * @param model The parameters to open the URL in the system browser
   */
  async openInSystemBrowser (url: string): Promise<void> {
     const androidOptions: AndroidSystemBrowserOptions = {
      showTitle: true,
      hideToolbarOnScroll: false,
      viewStyle: AndroidViewStyle.BOTTOM_SHEET,
      bottomSheetOptions: null,
      startAnimation: AndroidAnimation.FADE_IN,
      exitAnimation: AndroidAnimation.FADE_OUT
    };
    const iosOptions: IOSSystemBrowserOptions = {
      closeButtonText: DismissStyle.CLOSE,
      viewStyle: IOSViewStyle.FULL_SCREEN,
      animationEffect: IOSAnimation.CROSS_DISSOLVE,
      enableBarsCollapsing: false,
      enableReadersMode: false
    };
    const systemBrowserOptions: SystemBrowserOptions = {
     android: androidOptions,
     iOS: iosOptions
    };
    await this.plugins.InAppBrowser.openInSystemBrowser({
      url,
      options: systemBrowserOptions
    });
  }
  /**
   * Opens the web content of the given URL in a separate browser, outside of your mobile application.
   * @param model The parameters to open the URL in the external browser
   */
  async openInExternalBrowser (url: string): Promise<void> {
    await this.plugins.InAppBrowser.openInExternalBrowser({ url });
  }
}

export default new InAppBrowser();